import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  Accordion,
  Table,
  ProgressBar,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaBuilding,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaTools,
  FaCalendar,
  FaCalendarCheck,
  FaCalendarDay,
  FaNotesMedical,
  FaSync,
  FaCheckCircle,
  FaTimesCircle,
  FaClipboardList,
  FaBolt,
  FaWater,
  FaGasPump,
  FaWifi,
  FaTruck,
  FaIndustry,
  FaFileContract,
  FaChartLine,
  FaInfoCircle,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const VendorUtility = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  // Removed bidTaskInfo state since we can't fetch it
  const [activeAccordionKey, setActiveAccordionKey] = useState('0'); // Changed to string for default opening
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  /* ================= FETCH VENDORS & UTILITIES ================= */
  const fetchVendorsAndUtilities = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/data/getVendorsAndUtilitiesByBidTask/${taskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setVendors(res.data.vendors || []);
        // Removed fetchBidTaskInfo call since API doesn't exist
      }
    } catch (error) {
      console.error('Fetch vendors error:', error);
      toast.error('Failed to load vendors & utilities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchVendorsAndUtilities();
    }
  }, [taskId]);

  /* ================= FORMATTERS ================= */
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getUtilityIcon = (service) => {
    const serviceLower = service.toLowerCase();
    if (serviceLower.includes('electric')) {
      return <FaBolt className="text-warning" />;
    } else if (serviceLower.includes('water')) {
      return <FaWater className="text-primary" />;
    } else if (serviceLower.includes('gas')) {
      return <FaGasPump className="text-danger" />;
    } else if (serviceLower.includes('internet') || serviceLower.includes('wifi')) {
      return <FaWifi className="text-info" />;
    } else if (serviceLower.includes('delivery') || serviceLower.includes('truck')) {
      return <FaTruck className="text-secondary" />;
    } else {
      return <FaTools className="text-dark" />;
    }
  };

  const getStatusBadge = (utility) => {
    if (utility.dateInstalled) {
      return <Badge bg="success" className="px-2 py-1">Completed</Badge>;
    } else if (utility.proposedInstallDate && new Date(utility.proposedInstallDate) < new Date()) {
      return <Badge bg="danger" className="px-2 py-1">Overdue</Badge>;
    } else if (utility.dateCalled && !utility.proposedInstallDate) {
      return <Badge bg="warning" className="px-2 py-1" text="dark">Scheduled</Badge>;
    } else {
      return <Badge bg="secondary" className="px-2 py-1">Pending</Badge>;
    }
  };

  /* ================= CALCULATIONS ================= */
  const calculateUtilityStats = () => {
    let total = 0;
    let completed = 0;
    let overdue = 0;
    let pending = 0;

    vendors.forEach(vendor => {
      vendor.utilities?.forEach(utility => {
        total++;
        if (utility.dateInstalled) {
          completed++;
        } else if (utility.proposedInstallDate && new Date(utility.proposedInstallDate) < new Date()) {
          overdue++;
        } else {
          pending++;
        }
      });
    });

    return { total, completed, overdue, pending };
  };

  const calculateProgress = () => {
    const stats = calculateUtilityStats();
    if (stats.total === 0) return 0;
    return Math.round((stats.completed / stats.total) * 100);
  };

  // Handle accordion toggle
  const handleAccordionToggle = (eventKey) => {
    setActiveAccordionKey(activeAccordionKey === eventKey ? null : eventKey);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading vendors & utilities...</span>
      </div>
    );
  }

  const stats = calculateUtilityStats();
  const progress = calculateProgress();

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Bid Estimates
              </Button>
              <h2 className="fw-bold mb-2">
                <FaTruck className="me-3" />
                Vendors & Utilities Management
              </h2>
              
              {/* Removed bidTaskInfo display section */}
              <div className="mb-3">
                <h4 className="mb-1">
                  <FaClipboardList className="me-2 text-primary" />
                  Task ID: {taskId?.substring(0, 16)}...
                </h4>
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <Badge bg="info" className="px-3 py-2">
                    <FaTruck className="me-2" />
                    Vendors & Utilities
                  </Badge>
                  <Badge bg="secondary" className="px-3 py-2">
                    <FaBuilding className="me-2" />
                    Task ID: {taskId?.substring(0, 12)}...
                  </Badge>
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div className="me-4">
                  <span className="text-muted me-2">Overall Progress:</span>
                  <Badge bg={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}>
                    {progress}%
                  </Badge>
                </div>
                <div>
                  <span className="text-muted me-2">Vendors:</span>
                  <Badge bg="dark">{vendors.length}</Badge>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={fetchVendorsAndUtilities}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* PROGRESS BAR */}
          <div className="mb-4">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Utilities Completion</small>
              <small className="text-muted">{progress}%</small>
            </div>
            <ProgressBar 
              now={progress} 
              variant={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}
              style={{ height: '8px' }}
            />
          </div>
        </Card.Body>
      </Card>

      {/* UTILITY STATS */}
      {vendors.length > 0 && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-light">
            <h4 className="mb-0">
              <FaChartLine className="me-2" />
              Utilities Overview
            </h4>
          </Card.Header>
          <Card.Body>
            <Row className="text-center">
              <Col md={3} sm={6} className="mb-4">
                <Card className="border-0 bg-primary bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2">{stats.total}</div>
                    <div className="text-muted">Total Utilities</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={3} sm={6} className="mb-4">
                <Card className="border-0 bg-success bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-success">{stats.completed}</div>
                    <div className="text-muted">Completed</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={3} sm={6} className="mb-4">
                <Card className="border-0 bg-warning bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-warning">{stats.pending}</div>
                    <div className="text-muted">Pending</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={3} sm={6} className="mb-4">
                <Card className="border-0 bg-danger bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-danger">{stats.overdue}</div>
                    <div className="text-muted">Overdue</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* VENDORS LIST */}
      {vendors.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaTruck size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Vendors Found</h4>
            <p className="text-muted mb-4">
              No vendors & utilities have been added to this bid task yet.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Accordion activeKey={activeAccordionKey} onSelect={handleAccordionToggle} className="mb-4">
          {vendors.map((vendor, vendorIndex) => (
            <Accordion.Item key={vendor._id} eventKey={vendorIndex.toString()}>
              <Accordion.Header>
                <div className="d-flex w-100 align-items-center">
                  <div className="me-3">
                    <Badge bg="light" text="dark" className="px-2 py-1">
                      {vendorIndex + 1}
                    </Badge>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-bold fs-5">{vendor.companyName}</div>
                      <div className="text-muted me-3">
                        <Badge bg="info" className="px-3 py-1">
                          {vendor.utilities?.length || 0} Utilities
                        </Badge>
                      </div>
                    </div>
                    <div className="small text-muted">
                      Contact: {vendor.contactPerson} • {vendor.email} • {vendor.phone}
                    </div>
                  </div>
                </div>
              </Accordion.Header>
              
              <Accordion.Body>
                <Row className="mb-4">
                  {/* Vendor Contact Information */}
                  <Col md={4}>
                    <Card className="h-100">
                      <Card.Header className="bg-light">
                        <h6 className="mb-0">
                          <FaBuilding className="me-2" />
                          Vendor Details
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        <ListGroup variant="flush">
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0">
                                <FaUser className="text-muted me-3" />
                              </div>
                              <div className="flex-grow-1">
                                <small className="text-muted d-block">Contact Person</small>
                                <div className="fw-medium">{vendor.contactPerson}</div>
                              </div>
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0">
                                <FaEnvelope className="text-muted me-3" />
                              </div>
                              <div className="flex-grow-1">
                                <small className="text-muted d-block">Email</small>
                                <div className="fw-medium">{vendor.email}</div>
                              </div>
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0">
                                <FaPhone className="text-muted me-3" />
                              </div>
                              <div className="flex-grow-1">
                                <small className="text-muted d-block">Phone</small>
                                <div className="fw-medium">{vendor.phone}</div>
                              </div>
                            </div>
                          </ListGroup.Item>
                          
                          <ListGroup.Item className="border-0 px-0 py-2">
                            <div className="d-flex align-items-center">
                              <div className="flex-shrink-0">
                                <FaFileContract className="text-muted me-3" />
                              </div>
                              <div className="flex-grow-1">
                                <small className="text-muted d-block">Vendor ID</small>
                                <div className="small text-muted">{vendor._id?.substring(0, 12)}...</div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Utilities Table */}
                  <Col md={8}>
                    <Card className="h-100">
                      <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                          <FaTools className="me-2" />
                          Utilities & Services ({vendor.utilities?.length || 0})
                        </h6>
                      </Card.Header>
                      <Card.Body className="p-0">
                        <div className="table-responsive">
                          <Table hover className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Service</th>
                                <th>Date Called</th>
                                <th>Proposed Install</th>
                                <th>Date Installed</th>
                                <th>Status</th>
                                <th>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vendor.utilities?.map((utility, utilIndex) => (
                                <tr key={utility._id}>
                                  <td>
                                    <Badge bg="light" text="dark" className="px-2 py-1">
                                      {utilIndex + 1}
                                    </Badge>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      {getUtilityIcon(utility.service)}
                                      <span className="ms-2 fw-medium">{utility.service}</span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="small">{formatDate(utility.dateCalled)}</div>
                                    {utility.dateCalled && (
                                      <small className="text-muted">
                                        <FaCalendarDay className="me-1" />
                                        Called
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    <div className={utility.proposedInstallDate ? 'fw-medium' : 'text-muted'}>
                                      {formatDate(utility.proposedInstallDate)}
                                    </div>
                                    {utility.proposedInstallDate && (
                                      <small className="text-muted">
                                        <FaCalendar className="me-1" />
                                        Proposed
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    <div className={utility.dateInstalled ? 'fw-bold text-success' : 'text-muted'}>
                                      {formatDate(utility.dateInstalled)}
                                    </div>
                                    {utility.dateInstalled && (
                                      <small className="text-success">
                                        <FaCalendarCheck className="me-1" />
                                        Installed
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    {getStatusBadge(utility)}
                                  </td>
                                  <td>
                                    {utility.notes ? (
                                      <div className="small text-muted">
                                        <FaNotesMedical className="me-1" />
                                        {utility.notes.length > 30 ? `${utility.notes.substring(0, 30)}...` : utility.notes}
                                      </div>
                                    ) : (
                                      <span className="text-muted small">No notes</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        
                        {/* Summary for this vendor */}
                        {vendor.utilities?.length > 0 && (
                          <div className="p-3 bg-light border-top">
                            <Row>
                              <Col md={4}>
                                <small className="text-muted">
                                  Showing {vendor.utilities.length} utilities for {vendor.companyName}
                                </small>
                              </Col>
                              <Col md={8}>
                                <div className="d-flex justify-content-end gap-3">
                                  <div className="text-center">
                                    <div className="fw-bold text-success">
                                      {vendor.utilities.filter(u => u.dateInstalled).length}
                                    </div>
                                    <small className="text-muted">Completed</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold text-warning">
                                      {vendor.utilities.filter(u => !u.dateInstalled && u.proposedInstallDate && new Date(u.proposedInstallDate) >= new Date()).length}
                                    </div>
                                    <small className="text-muted">Scheduled</small>
                                  </div>
                                  <div className="text-center">
                                    <div className="fw-bold text-danger">
                                      {vendor.utilities.filter(u => !u.dateInstalled && u.proposedInstallDate && new Date(u.proposedInstallDate) < new Date()).length}
                                    </div>
                                    <small className="text-muted">Overdue</small>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* SUMMARY FOOTER */}
      {vendors.length > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="bg-light">
            <Row className="align-items-center">
              <Col md={4}>
                <div className="small text-muted">
                  Showing {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} with {stats.total} utilities
                </div>
              </Col>
              <Col md={8}>
                <div className="d-flex justify-content-end gap-4">
                  <div className="text-center">
                    <div className="fw-bold text-primary">{stats.total}</div>
                    <small className="text-muted">Total Utilities</small>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-success">{stats.completed}</div>
                    <small className="text-muted">Completed</small>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-warning">{stats.pending}</div>
                    <small className="text-muted">Pending</small>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-danger">{stats.overdue}</div>
                    <small className="text-muted">Overdue</small>
                  </div>
                  <div className="text-center">
                    <div className={`fw-bold ${progress === 100 ? 'text-success' : progress >= 50 ? 'text-info' : 'text-warning'}`}>
                      {progress}%
                    </div>
                    <small className="text-muted">Overall Progress</small>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* EMPTY STATE INFO */}
      {vendors.length === 0 && (
        <Card className="border-0 shadow-sm mt-4">
          <Card.Body>
            <Alert variant="info" className="mb-0">
              <div className="d-flex align-items-center">
                <FaInfoCircle className="me-3" size={24} />
                <div>
                  <h5 className="alert-heading mb-2">Vendors & Utilities Management</h5>
                  <p className="mb-0">
                    This page displays all vendors and utility services associated with the selected bid task.
                    Vendors can provide multiple utility services (electricity, water, gas, internet, etc.)
                    with their installation schedules and status tracking.
                  </p>
                </div>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default VendorUtility;